import { Eye, EyeOff, Loader2 } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate } from "react-router";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useSignup } from "@/lib/api";
import { logger } from "@/lib/logger";

function getPasswordStrength(pw: string): { label: string; color: string; width: string } {
  if (pw.length === 0) {
    return { label: "", color: "", width: "0%" };
  }
  let score = 0;
  if (pw.length >= 8) {
    score++;
  }
  if (pw.length >= 12) {
    score++;
  }
  if (/[A-Z]/.test(pw)) {
    score++;
  }
  if (/[0-9]/.test(pw)) {
    score++;
  }
  if (/[^A-Za-z0-9]/.test(pw)) {
    score++;
  }
  if (score <= 1) {
    return { label: "Weak", color: "bg-red-500", width: "20%" };
  }
  if (score <= 2) {
    return { label: "Fair", color: "bg-orange-500", width: "40%" };
  }
  if (score <= 3) {
    return { label: "Good", color: "bg-yellow-500", width: "60%" };
  }
  if (score <= 4) {
    return { label: "Strong", color: "bg-green-500", width: "80%" };
  }
  return { label: "Very strong", color: "bg-green-600", width: "100%" };
}

export function SignupPage() {
  const navigate = useNavigate();
  const signupMutation = useSignup();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");

  const strength = getPasswordStrength(password);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }

    logger.debug("[signup] submitting for", email);
    try {
      await signupMutation.mutateAsync({ email, password, firstName, lastName });
      logger.debug("[signup] success → redirecting to /verify-email");
      navigate(`/verify-email?email=${encodeURIComponent(email)}`);
    } catch (err) {
      const detail =
        (err as { body?: { detail?: string } } | undefined)?.body?.detail ?? "Registration failed.";
      logger.debug("[signup] failed:", detail);
      setError(detail);
    }
  };

  return (
    <div className="bg-background flex min-h-screen items-center justify-center px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <img
            src="/brand/logo.png"
            alt="Board of Innovation"
            className="mx-auto mb-2 h-10 w-auto object-contain"
          />
          <CardTitle className="font-heading text-primary text-2xl font-bold">
            Case Study Generator
          </CardTitle>
          <p className="text-muted text-sm">Create your BOI account</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label htmlFor="firstName" className="text-foreground text-sm font-medium">
                  First name
                </label>
                <Input
                  id="firstName"
                  type="text"
                  autoComplete="given-name"
                  placeholder="Alex"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <label htmlFor="lastName" className="text-foreground text-sm font-medium">
                  Last name
                </label>
                <Input
                  id="lastName"
                  type="text"
                  autoComplete="family-name"
                  placeholder="Morgan"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <label htmlFor="email" className="text-foreground text-sm font-medium">
                Email
              </label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                placeholder="john.doe@boardofinnovation.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="password" className="text-foreground text-sm font-medium">
                Password
              </label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  placeholder="At least 8 characters"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="text-muted hover:text-foreground absolute top-1/2 right-3 -translate-y-1/2 transition-colors"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {password.length > 0 && (
                <div className="space-y-1">
                  <div className="bg-muted h-1.5 w-full rounded-full">
                    <div
                      className={`h-1.5 rounded-full transition-all ${strength.color}`}
                      style={{ width: strength.width }}
                    />
                  </div>
                  <p className="text-muted text-xs">{strength.label}</p>
                </div>
              )}
            </div>
            {error && <p className="text-destructive text-sm">{error}</p>}
            <Button
              type="submit"
              disabled={signupMutation.isPending}
              className="bg-accent text-accent-foreground hover:bg-accent/90 w-full"
            >
              {signupMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Creating account…
                </>
              ) : (
                "Sign up"
              )}
            </Button>
            <p className="text-muted text-center text-sm">
              Already have an account?{" "}
              <Link to="/login" className="text-accent hover:underline">
                Sign in
              </Link>
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
